<template>
  <div v-if="isViewingCurrentMonth" class="quick-transaction-tab d-none d-lg-block">
    <!-- Collapsed Tab Button -->
    <div 
      v-if="!isExpanded" 
      @click="expandTab"
      class="tab-button"
      :class="{ 'pulse': shouldPulse }"
    >
      <div class="tab-icon">
        <i class="fa-solid fa-plus"></i>
      </div>
      <div class="tab-text">QUICK ADD</div>
    </div>

    <!-- Expanded Panel -->
    <div 
      v-show="isExpanded" 
      class="expanded-panel card"
      :class="{ 'show': isExpanded }"
    >
      <!-- Card Header (Required Pattern) -->
      <div class="card-header">
        <div class="header-icon">
          <i class="fa-solid fa-receipt"></i>
        </div>
        <div class="header-content">
          <h3 class="card-title">New Transaction</h3>
          <p class="card-subtitle">Describe any new transactions</p>
        </div>
        <button @click="collapseTab" class="close-btn">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <!-- Panel Body -->
      <div class="panel-body">
        <NaturalLanguageTransaction 
          :auto-confirm="true"
          @processing-started="handleProcessingStarted"
          @transaction-processed="handleTransactionProcessed"
          @transactions-confirmed="handleTransactionsConfirmed"
        />
      </div>
    </div>

    <!-- Backdrop -->
    <div 
      v-show="isExpanded" 
      @click="collapseTab"
      class="tab-backdrop"
    ></div>
  </div>
</template>

<script>
import NaturalLanguageTransaction from './NaturalLanguageTransaction.vue';
import { useBudgetData } from '../composables/useBudgetData';

export default {
  name: 'QuickTransactionTab',
  components: {
    NaturalLanguageTransaction
  },
  emits: ['transaction-processed', 'transactions-confirmed', 'processing-started'],
  setup() {
    const { isViewingCurrentMonth } = useBudgetData();
    return {
      isViewingCurrentMonth
    };
  },
  data() {
    return {
      isExpanded: false,
      shouldPulse: false,
      handleEscape: null
    }
  },
  methods: {
    expandTab() {
      this.isExpanded = true;
      this.shouldPulse = false;
      // Focus on the input after expansion animation
      this.$nextTick(() => {
        const input = this.$el.querySelector('.natural-input');
        if (input) {
          setTimeout(() => input.focus(), 300);
        }
      });
    },

    collapseTab() {
      this.isExpanded = false;
    },

    handleProcessingStarted(data) {
      this.$emit('processing-started', data);
      // Immediately collapse the panel when processing starts
      this.collapseTab();
    },

    handleTransactionProcessed(data) {
      this.$emit('transaction-processed', data);
    },

    handleTransactionsConfirmed(data) {
      this.$emit('transactions-confirmed', data);
      // Panel already collapsed on processing start, no need to collapse again
    },

    // Method to trigger pulse animation (can be called from parent)
    triggerPulse() {
      this.shouldPulse = true;
      setTimeout(() => {
        this.shouldPulse = false;
      }, 2000);
    }
  },
  mounted() {
    // Handle ESC key to close panel
    this.handleEscape = (event) => {
      if (event.key === 'Escape' && this.isExpanded) {
        this.collapseTab();
      }
    };
    document.addEventListener('keydown', this.handleEscape);
  },
  
  beforeUnmount() {
    // Cleanup event listener
    if (this.handleEscape) {
      document.removeEventListener('keydown', this.handleEscape);
    }
  }
}
</script>

<style scoped>
.quick-transaction-tab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1040;
}

/* Collapsed Tab Button */
.tab-button {
  background-color: var(--btn-primary-color);
  color: white;
  border-radius: 50px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  box-shadow: 0 8px 25px color-mix(in srgb, var(--btn-primary-color) 10%, transparent);
  transition: all 0.3s ease;
  border: none;
  min-width: 140px;
  justify-content: center;
}

.tab-button:hover {
  background-color: var(--btn-primary-color);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px color-mix(in srgb, var(--btn-primary-color) 10%, transparent);
}

.tab-button.pulse {
  animation: pulseGlow 2s ease-in-out;
}

@keyframes pulseGlow {
  0%, 100% {
    box-shadow: 0 8px 25px rgba(54, 162, 235, 0.3);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 12px 40px rgba(54, 162, 235, 0.6);
    transform: scale(1.05);
  }
}

.tab-icon {
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-text {
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
}

/* Expanded Panel */
.expanded-panel {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 420px;
  max-height: 80vh;
  background: var(--light-card);
  border: none;
  border-top: 7px solid var(--light-border);
  border-radius: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transform: translateY(100%) scale(0.8);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.expanded-panel.show {
  transform: translateY(0) scale(1);
  opacity: 1;
}

/* Card Header (Shared Pattern) */
.card-header {
  background: none;
  border: none;
  padding: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem;
  border-bottom: 1px solid var(--light-border);
}

.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
}

.header-content {
  flex: 1;
  text-align: left;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--light-text);
  margin: 0;
  line-height: 1.2;
}

.card-subtitle {
  font-size: 0.85rem;
  color: var(--light-text-muted);
  margin: 0.25rem 0 0;
  line-height: 1.2;
}

.close-btn {
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.close-btn:hover {
  background: var(--light-border);
  color: var(--light-text);
}

/* Panel Body */
.panel-body {
  padding: 0 1rem 1rem 1rem;
  max-height: calc(80vh - 100px);
  overflow-y: auto;
}

/* Override NaturalLanguageTransaction styles for panel */
.panel-body :deep(.card) {
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
}

.panel-body :deep(.card-header) {
  display: none;
}

.panel-body :deep(.card-body) {
  padding: 0;
}

/* Backdrop */
.tab-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
  opacity: 0;
  animation: fadeInBackdrop 0.3s ease forwards;
}

@keyframes fadeInBackdrop {
  to {
    opacity: 1;
  }
}

/* Dark Mode Support */
:global([data-theme]) .expanded-panel {
  background: var(--light-card);
  border-top-color: var(--light-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

:global([data-theme]) .card-header {
  background: none;
  border-bottom-color: var(--light-border);
}

:global([data-theme]) .header-icon {
  color: var(--light-text);
  border-color: var(--light-text);
}

:global([data-theme]) .close-btn:hover {
  background: var(--light-border);
}

:global([data-theme]) .tab-backdrop {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsive - Hide on mobile/tablet */
@media (max-width: 1199px) {
  .quick-transaction-tab {
    display: none !important;
  }
}

/* Adjust panel size for smaller desktop screens */
@media (max-width: 1400px) {
  .expanded-panel {
    width: 380px;
    right: 1.5rem;
    bottom: 1.5rem;
  }
  
  .tab-button {
    right: 1.5rem;
    bottom: 1.5rem;
  }
}
</style>